/**
 * launcherThumbnail — capture + upload of a character's launcher-card thumbnail
 * (ENG-1079).
 *
 * The image is a snapshot of the live avatar viewport. Capturing it needs the
 * WebGL renderer + scene + camera, which only exist inside the R3F Canvas, so
 * <LauncherThumbnailCapturer/> (mounted in App's Canvas) registers a capturer
 * here via setLauncherThumbnailCapturer — the same module-singleton bridge
 * pattern materialThumbnail.js uses. Nothing on the render hot path: the
 * capturer only runs when a capture is explicitly requested.
 *
 * The captured JPEG is uploaded to the asset-manager (server is the source of
 * truth — a launcher thumbnail is org-shared, so it cannot live in
 * localStorage). Failures are LOUD (throw + console.error); the launcher falls
 * back to its pipeline glyph, never a broken image.
 */

import { uploadCharacterThumbnail } from './assetManagerApi.js'
import { clearCharactersCache } from './characterState.js'

let _capturer = null

/** Registered by the in-Canvas <LauncherThumbnailCapturer/>. Returns an
 *  unregister fn. The capturer takes ({ size }) and returns a JPEG data URL
 *  (or null when nothing has rendered yet). */
export function setLauncherThumbnailCapturer(fn) {
  _capturer = fn
  return () => { if (_capturer === fn) _capturer = null }
}

/** True when a capturer is mounted (the Canvas is up). The menu action uses
 *  this to disable itself with a reason rather than fail silently. */
export function canCaptureLauncherThumbnail() {
  return typeof _capturer === 'function'
}

/** Capture the current viewport as a portrait JPEG data URL, or null when the
 *  viewport hasn't rendered a real frame yet (the auto-capture path retries on
 *  null). Async: the capturer grabs the live composited frame off the canvas. */
export async function captureLauncherThumbnailDataUrl(opts = {}) {
  if (typeof _capturer !== 'function') return null
  try {
    return (await _capturer(opts)) ?? null
  } catch (e) {
    console.error('[launcherThumbnail] capture failed:', e)
    return null
  }
}

/**
 * Upload an already-captured data URL as `slug`'s launcher thumbnail and drop
 * the character-list cache so the launcher re-fetches. Returns the stored URL.
 * Split out from the capture so the auto path can retry capture (waiting for
 * the scene to finish compositing) and upload only once it has real pixels.
 */
export async function uploadThumbnailDataUrl(slug, dataUrl) {
  if (!slug) throw new Error('uploadThumbnailDataUrl: no character slug')
  if (!dataUrl) throw new Error('uploadThumbnailDataUrl: no image')
  const blob = await (await fetch(dataUrl)).blob()
  const form = new FormData()
  form.append('file', blob, 'thumbnail.jpg')
  const res = await uploadCharacterThumbnail(slug, form)
  clearCharactersCache()
  return res?.url ?? null
}

/**
 * Capture the current viewport and upload it as `slug`'s launcher thumbnail.
 * Returns the stored URL. Throws (loudly) when there is no character, nothing
 * rendered to capture, or the upload fails — the manual menu path surfaces
 * that, never a silent success.
 */
export async function captureAndUploadThumbnail(slug, opts = {}) {
  if (!slug) throw new Error('captureAndUploadThumbnail: no character slug')
  const dataUrl = await captureLauncherThumbnailDataUrl(opts)
  if (!dataUrl) {
    throw new Error('captureAndUploadThumbnail: nothing to capture (viewport not ready)')
  }
  return uploadThumbnailDataUrl(slug, dataUrl)
}
